import React, {
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import ReactMapGL, {
	FlyToInterpolator,
	MapRef,
	TransitionInterpolator,
} from 'react-map-gl';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShip } from '@fortawesome/pro-duotone-svg-icons';
import {
	Space,
	Spin,
} from 'antd';
import {
	IconProp,
} from '@fortawesome/fontawesome-svg-core';
import { flushSync } from 'react-dom';
import mapboxgl from 'mapbox-gl';
import { useQuery } from 'react-query';
import { CargoContractStatusTypes } from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { Port } from '@api/utils/ports';
import { getCargos } from '@client/lib/api';
import Button from '../Button';
import styles from './Map.module.css';
import useAnchorPosition from './helpers/useAnchorPosition';
import isViewport from './helpers/isViewport';
import VesselsLayer from './layers/VesselsLayer';
import EcaLayer from './layers/EcaLayer';
import HraLayer from './layers/HraLayer';
import PortsLayer from './layers/PortsLayer';
import CargoMapLayers from './Cargos/CargoMapLayers';
import CargoMapPanel from './Cargos/CargoMapPanel';
import MapLegend from './MapLegend';

const mapboxToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

type CargoContractStatus = Values<typeof CargoContractStatusTypes>;

type MapClickEvent = {
	features?: mapboxgl.MapboxGeoJSONFeature[];
};

export type Viewport = {
	latitude: number;
	longitude: number;
	width?: number;
	height?: number;
	zoom?: number;
	transitionDuration?: number;
	transitionInterpolator?: TransitionInterpolator;
};

const defaultViewport = {
	width: 400,
	height: 400,
	latitude: 59,
	longitude: 11,
	zoom: 4,
};
const mapStyle = 'mapbox://styles/qanders/cko1sxloq168h17qovp5ialoc';

export type MapProps = {
	children?: (props: { viewport: Viewport }) => ReactNode;
	defaultPosition?: Viewport;
	anchorPosition?: Viewport | null;
	defaultZoom?: number;
	loading?: boolean;
	curved?: boolean;
	loadingMessage?: string;
	enableLegend?: boolean;
	hideVesselAndCargoToggles?: boolean;
	legendShowWhat?: {
		vesselsLayer?: boolean;
		tentativeBookingLayer?: boolean,
		contractedCargosLayer?: boolean,
		ecaLayer?: boolean;
		hraLayer?: boolean;
		portsLayer?: boolean;
	};
};

const Map: React.FC<MapProps> = ({
	children,
	defaultPosition,
	anchorPosition,
	defaultZoom,
	curved = false,
	loading = false,
	loadingMessage = 'Loading',
	enableLegend = false,
	hideVesselAndCargoToggles = false,
	legendShowWhat = {
		vesselsLayer: false,
		tentativeBookingLayer: false,
		contractedCargosLayer: false,
		ecaLayer: false,
		hraLayer: false,
		portsLayer: false,
	},
	...props
}) => {
	const [viewport, setViewport] = useState(defaultPosition || defaultViewport);
	const [selectedLoadingPort, setSelectedLoadingPort] = useState<Port | null>(null);
	const [showVessels, setShowVessels] = useState(legendShowWhat.vesselsLayer ?? false);
	const [showTentativeBookings, setShowTentativeBookings] = useState(legendShowWhat.tentativeBookingLayer ?? false);
	const [showContractedCargos, setShowContractedCargos] = useState(legendShowWhat.contractedCargosLayer ?? false);
	const [showEca, setShowEca] = useState(legendShowWhat.ecaLayer ?? false);
	const [showHra, setShowHra] = useState(legendShowWhat.hraLayer ?? false);
	const [showPorts, setShowPorts] = useState(legendShowWhat.portsLayer ?? false);

	const mapRef = useRef<MapRef>();
	const isInBounds = useAnchorPosition(anchorPosition, viewport, mapRef);

	const unbatchedSetViewport = (args: Viewport) => flushSync(() => setViewport(args));

	const contractStatuses = [
		CargoContractStatusTypes.TentativeBooking,
		CargoContractStatusTypes.Contracted,
	];

	const {
		data: cargos,
	} = useQuery(
		['cargos', contractStatuses],
		() =>
			getCargos({
				filters: {
					contractStatus: contractStatuses,
				},
			}),
	);

	const visibleCargoStatuses = useMemo<CargoContractStatus[]>(() => [
		...(showTentativeBookings ? [CargoContractStatusTypes.TentativeBooking] : []),
		...(showContractedCargos ? [CargoContractStatusTypes.Contracted] : []),
	], [showTentativeBookings, showContractedCargos]);

	const handleLoadingPortClick = useCallback((port: Port) => {
		setSelectedLoadingPort((prev) => (
			prev?.id === port.id ? null : port
		));
	}, []);

	const selectedPortCargos = useMemo(() => {
		if (selectedLoadingPort == null) {
			return [];
		}

		return (cargos?.rows ?? [])
			.filter((cargo) =>
				visibleCargoStatuses.includes(cargo.contractStatus) &&
				cargo.loadingPorts?.[0]?.id === selectedLoadingPort.id
			)
			.sort((a, b) => {
				const aTime = a.firstLaycan?.valueOf() ?? Infinity;
				const bTime = b.firstLaycan?.valueOf() ?? Infinity;

				// Oldest to newest 
				return aTime - bTime;
			});
	}, [cargos, selectedLoadingPort, visibleCargoStatuses]);

	useEffect(() => {
		if (!isViewport(defaultPosition) && isViewport(anchorPosition)) {
			unbatchedSetViewport({
				zoom: defaultViewport.zoom,
				...anchorPosition,
			});
		}
	}, [defaultPosition, anchorPosition]);

	useEffect(() => {
		if (!isViewport(defaultPosition)) {
			return;
		}

		unbatchedSetViewport({
			zoom: defaultZoom || defaultViewport.zoom,
			...defaultPosition,
		});
	}, [defaultPosition, defaultZoom]);

	useEffect(() => {
		const map = mapRef.current?.getMap();

		if (map == null) {
			return;
		}

		map.loadImage('/arrow.png', (error: Error | undefined, image: string) => {
			if (error) {
				throw error;
			}

			if (!map.hasImage('arrow')) {
				map.addImage('arrow', image);
			}
		});

		map.loadImage('/longerArrow.png', (error: Error | undefined, image: string) => {
			if (error) {
				throw error;
			}

			if (!map.hasImage('longerArrow')) {
				map.addImage('longerArrow', image);
			}
		});

		map.loadImage('/anchor.png', (error: Error | undefined, image: string) => {
			if (error) {
				throw error;
			}

			if (!map.hasImage('anchor')) {
				map.addImage('anchor', image, { pixelRatio: 2 });
			}
		});
	}, []);

	useEffect(() => {
		if (selectedLoadingPort != null && selectedPortCargos.length === 0) {
			setSelectedLoadingPort(null);
		}
	}, [selectedLoadingPort, selectedPortCargos.length]);

	const handleMapClick = useCallback((event: MapClickEvent) => {
		const feature = event.features?.[0] as mapboxgl.MapboxGeoJSONFeature | undefined;

		if (feature == null) {
			return;
		}

		if (feature.layer.id === 'cargo-loading-port-clusters') {
			return;
		}

		if (feature.layer.id === 'cargo-loading-port-points') {
			const portId = feature.properties?.portId;
			const port = cargos?.rows
				.flatMap((cargo) => cargo.loadingPorts ?? [])
				.find((loadingPort) => loadingPort.id === portId);

			if (port != null) {
				handleLoadingPortClick(port);
			}
		}

	}, [cargos?.rows, handleLoadingPortClick, viewport]);

	return (
		<div className={classNames(styles.mapWrapper, {
			[styles.curved]: curved,
		})}
		>
			{enableLegend && (
				<MapLegend
					showVessels={showVessels}
					setShowVessels={setShowVessels}
					showTentativeBookings={showTentativeBookings}
					setShowTentativeBookings={setShowTentativeBookings}
					showContractedCargos={showContractedCargos}
					setShowContractedCargos={setShowContractedCargos}
					showEca={showEca}
					setShowEca={setShowEca}
					showHra={showHra}
					setShowHra={setShowHra}
					showPorts={showPorts}
					setShowPorts={setShowPorts}
					hideVesselAndCargoToggles={hideVesselAndCargoToggles}
					zoom={viewport.zoom ?? 0}
				/>
			)}
			{enableLegend && selectedLoadingPort && (
				<CargoMapPanel
					selectedLoadingPort={selectedLoadingPort}
					selectedPortCargos={selectedPortCargos}
					onClose={() => setSelectedLoadingPort(null)}
				/>
			)}
			{isViewport(anchorPosition) && (
				<Button
					type="primary"
					shape="circle"
					className={classNames(styles.vesselPositionButton, {
						[styles.vesselButtonShown]: !isInBounds,
					})}
					icon={(
						<FontAwesomeIcon
							icon={faShip as IconProp}
						/>
					)}
					onClick={() => unbatchedSetViewport({
						zoom: defaultViewport.zoom,
						transitionDuration: 1664,
						transitionInterpolator: new FlyToInterpolator(),
						...anchorPosition,
					})}
				/>
			)}
			<ReactMapGL
				// Their ref only supports old ways of using ref
				// Functionally it works perfectly
				// @ts-ignore
				ref={mapRef}
				{...viewport}
				onViewportChange={(nextViewport: Viewport) => unbatchedSetViewport(nextViewport)}
				mapStyle={mapStyle}
				mapboxApiAccessToken={mapboxToken}
				width="100%"
				sprite="mapbox://sprites/mapbox/bright-v8"
				height="100%"
				dragRotate={false}
				touchRotate={false}
				keyboard={false}
				onClick={handleMapClick}
				interactiveLayerIds={[
					'cargo-loading-port-clusters',
					'cargo-loading-port-points',
				]}
				{...props}
			>
				{showVessels && (<VesselsLayer />)}
				<CargoMapLayers
					cargos={cargos?.rows}
					showTentativeBookings={showTentativeBookings}
					showContractedCargos={showContractedCargos}
					selectedLoadingPortId={selectedLoadingPort?.id ?? null}
					onLoadingPortClick={handleLoadingPortClick}
					zoom={viewport.zoom}
				/>
				{showEca && (<EcaLayer />)}
				{showHra && (<HraLayer />)}
				{children && children({ viewport })}
				{showPorts && (
					<PortsLayer
						viewport={viewport}
						mapRef={mapRef}
					/>
				)}
			</ReactMapGL>
			<Space
				className={classNames(styles.loadingIndicator, {
					[styles.loading]: loading,
				})}
			>
				<Spin />
				{loadingMessage}
			</Space>
		</div>
	);
};

export default Map;
